import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ManageOrders = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "search-view-orders",
        "edit-order-details",
        "manage-orders",
        "print-orders",
        "abandoned-checkouts",
        "view-abandoned-checkouts",
        "automated-recovery-emails",
        "manual-recovery-links",
        "track-recovery-status",
        "recovery-status",
        "cancel-order",
        "create-return",
        "create-exchange",
        "order-refund",
        "export-orders",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Manage Orders | xPage Help Center"
        description="Learn how to efficiently manage orders in xPage. View, edit, process, print, export orders, handle returns, exchanges, refunds, and recover abandoned checkouts."
        keywords="xPage, manage orders, view orders, edit orders, print orders, export orders, returns, exchanges, refunds, abandoned checkouts, recovery emails"
        canonical="https://help.xpage.ai/help/store-operation/Manage%20orders"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Manage orders
          </h1>
          <p className="text-sm text-systemLabel">
            Orders in xPage represent purchase requests submitted by customers
            through your store. Each order includes customer details, purchased
            products, payment status, shipping details, and more.
          </p>
          <p className="text-sm text-systemLabel">
            In the xPage admin, all orders can be managed from the Orders page.
            From here, you can view, edit, process, print, and export orders,
            create draft orders, collect payments, track abandoned carts, and
            handle after-sales service such as returns, refunds, and exchanges.
          </p>
        </header>

        {/* Search and View Orders Section */}
        <section id="search-view-orders" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Search and view orders
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">Log in to the xPage admin.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Orders in the left navigation bar to open the order
                  list.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  By default, the list shows all orders sorted by creation time
                  in descending order (newest first).
                </span>
              </li>
            </ol>

            <p className="text-sm text-systemLabel mb-4">
              Within the Order list, you can:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li>Click an Order ID to open the Order details page.</li>
              <li>
                Click on Create time to sort by ascending or descending date.
              </li>
              <li>
                Click on Product to see product details (image, name, unit
                price, quantity).
              </li>
            </ul>

            <p className="text-sm text-systemLabel mb-4">
              In the function area above the list, you can:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Click + to customize the order list view.</li>
              <li>
                Use the Search bar to look up orders by number, product, or
                customer name.
              </li>
              <li>Click Edit header to adjust which columns display.</li>
              <li>
                Use Filter to refine results by date, channel, payment status,
                shipping method, product type, tags, and more.
              </li>
              <li>Use Sort to arrange orders by creation time.</li>
            </ul>
          </div>
        </section>

        {/* Edit Order Details Section */}
        <section id="edit-order-details" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Edit order details
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              From the Order details page, you can update:
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Field</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">Action</strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Notes</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Add/edit notes, then click Save.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Tags</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Add or select tags, click + to save, or remove using the x.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Contact info
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Edit customer details, then click Save.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Shipping address
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Edit and save changes.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Billing address
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Edit and save changes.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Manage Orders Section */}
        <section id="manage-orders" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manage orders
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              You can update order statuses directly from the Order list:
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Manage orders in bulk
            </h3>
            <p className="text-sm text-systemLabel mb-4">
              Select multiple orders by checking their boxes, then perform bulk
              actions:
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Action</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      Description
                    </strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Mark as paid
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Updates unpaid orders to paid. Orders that fail remain
                    unchanged.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Mark as shipped
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Updates unshipped or partially shipped orders. Failed ones
                    remain unchanged.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Cancel orders
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Cancels unpaid/unshipped orders. Failed ones remain
                    unchanged.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Print Orders Section */}
        <section id="print-orders" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Print orders
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              You can print orders individually or in bulk:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                <strong>Order list:</strong> Select one or multiple orders, then
                click Print order. Review in the print preview and click Print.
              </li>
              <li>
                <strong>Order details page → Click More → Print order,</strong>{" "}
                verify details, and click Print.
              </li>
            </ul>
          </div>
        </section>

        {/* Abandoned Checkouts Section */}
        <section id="abandoned-checkouts" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Abandoned checkouts (Abandoned carts)
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              An abandoned cart occurs when a visitor reaches the checkout page
              and enters contact information (like email or phone number) but
              doesn't complete the purchase within a set timeframe, commonly 30
              minutes. xPage captures these cases to help you recover potential
              lost sales.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Abandoned cart rules
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                The customer must enter their email or phone at checkout to
                qualify as an abandoned cart.
              </li>
              <li>
                Abandoned cart data is retained for 3 months. If not recovered
                within that window, it's automatically deleted.
              </li>
              <li>Carts with a total value of $0.00 are not captured.</li>
              <li>
                Products that are out of stock or unpublished at the time of
                abandonment will not be included in recovery attempts.
              </li>
              <li>
                The cart itself isn't stored; only the checkout details and
                items listed at checkout are captured.
              </li>
            </ul>
          </div>
        </section>

        {/* View Abandoned Checkouts Section */}
        <section id="view-abandoned-checkouts" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            View abandoned checkouts
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to the xPage admin and navigate to Orders → Abandoned
                  Checkouts.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Browse the Abandoned List and click on any checkout's ID to
                  view its full details.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Manual Recovery Links Section */}
        <section id="manual-recovery-links" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manually share abandoned checkout links
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              You can also manually recover carts by sending the checkout link
              to a customer:
            </p>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Go to Orders → Abandoned Checkouts and open a specific record.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Copy Checkout URL.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Share the link via email or message—customers can complete
                  checkout directly using this link.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Track Recovery Status Section */}
        <section id="track-recovery-status" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Track recovery status
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Within each abandoned checkout's details page:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>View associated automation workflows.</li>
              <li>See if recovery emails have been sent or opened.</li>
              <li>
                If automation isn't yet enabled, you'll see a prompt to activate
                it.
              </li>
            </ul>
          </div>
        </section>

        {/* Recovery Status Section */}
        <section id="recovery-status" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Recovery status (when the cart is considered recovered)
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              A checkout is marked as recovered when a customer completes the
              purchase via the recovery email or manually revisits their
              checkout link and succeeds.
            </p>
          </div>
        </section>

        {/* Cancel Order Section */}
        <section id="cancel-order" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Cancel an order
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              In the xPage admin, you can cancel an order if the customer
              changes their mind or if you're unable to fulfill it. Canceled
              orders will not be shipped, and payments will be refunded if
              applicable.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to your xPage admin and go to Orders.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select the order you want to cancel.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Cancel order.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Confirm the cancellation.</span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Notes:</span> If the order has
                already been paid, ensure you process a refund (see Order
                refund). Canceled orders will still be visible in your order
                history but marked as Canceled.
              </p>
            </div>
          </div>
        </section>

        {/* Create a Return Section */}
        <section id="create-return" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Create a return
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Returns allow customers to send back products they are unsatisfied
              with or that arrived damaged.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to your xPage admin and go to Orders.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Open the order details page for the order you want to manage.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Create return.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select the products being returned and specify the reason.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Confirm.</span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> Returned items are
                tracked in the order details. You may need to create a refund
                after a return depending on your policy.
              </p>
            </div>
          </div>
        </section>

        {/* Create an Exchange Section */}
        <section id="create-exchange" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Create an exchange
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Exchanges allow customers to replace one product with another.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  In the xPage admin, go to Orders and open the relevant order.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Create exchange.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select the items to be exchanged and choose the replacement
                  products.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Confirm the exchange and update the order.
                </span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Notes:</span> Exchanges may
                include price differences (e.g., customer pays the difference if
                new item costs more). Shipping for exchanged products can be
                configured in your Shipping settings.
              </p>
            </div>
          </div>
        </section>

        {/* Order Refund Section */}
        <section id="order-refund" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Order refund
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Refunds allow you to return payments to customers after a
              cancellation, return, or exchange.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  In the xPage admin, go to Orders.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select the order that requires a refund.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Order refund.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Enter the refund amount (full or partial).
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Confirm the refund.</span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tips:</span> Refunds are
                processed back to the original payment method. Partial refunds
                are possible for specific items or amounts.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#search-view-orders"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "search-view-orders"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Search & View Orders
              </a>
            </li>
            <li>
              <a
                href="#edit-order-details"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "edit-order-details"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Edit Order Details
              </a>
            </li>
            <li>
              <a
                href="#manage-orders"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "manage-orders"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Manage Orders
              </a>
            </li>
            <li>
              <a
                href="#print-orders"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "print-orders"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Print Orders
              </a>
            </li>
            <li>
              <a
                href="#abandoned-checkouts"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "abandoned-checkouts"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Abandoned Checkouts
              </a>
            </li>
            <li>
              <a
                href="#view-abandoned-checkouts"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "view-abandoned-checkouts"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                View Abandoned Checkouts
              </a>
            </li>
            <li>
              <a
                href="#automated-recovery-emails"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "automated-recovery-emails"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Automated Recovery Emails
              </a>
            </li>
            <li>
              <a
                href="#manual-recovery-links"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "manual-recovery-links"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Manual Recovery Links
              </a>
            </li>
            <li>
              <a
                href="#track-recovery-status"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "track-recovery-status"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Track Recovery Status
              </a>
            </li>
            <li>
              <a
                href="#recovery-status"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "recovery-status"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Recovery Status
              </a>
            </li>
            <li>
              <a
                href="#cancel-order"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "cancel-order"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Cancel Order
              </a>
            </li>
            <li>
              <a
                href="#create-return"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "create-return"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Create Return
              </a>
            </li>
            <li>
              <a
                href="#create-exchange"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "create-exchange"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Create Exchange
              </a>
            </li>
            <li>
              <a
                href="#order-refund"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "order-refund"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Order Refund
              </a>
            </li>
            <li>
              <a
                href="#export-orders"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "export-orders"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Export Orders
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ManageOrders;
